'use client';

import { usePathname } from 'next/navigation';
import { inApp } from '@utils/platformDetection.client';
import { useIsMobile } from './use-mobile';

const pathMatches = (
  pathname: string | null,
  path: string,
  exact?: boolean
): boolean => {
  if (!pathname) return false;
  const normalized = path.endsWith('/') ? path.slice(0, -1) : path;
  if (exact) {
    return pathname === normalized || pathname === `${normalized}/`;
  }
  return pathname === normalized || pathname.startsWith(`${normalized}/`);
};

const pathMatchesAny = (
  pathname: string | null,
  paths: readonly { path: string; exact?: boolean }[]
): boolean =>
  paths.some(({ path, exact }) => pathMatches(pathname, path, exact));

const ALWAYS_HIDE_PATHS: readonly { path: string; exact?: boolean }[] = [
  { path: '/quote', exact: false },
  { path: '/cars/deal-confirmation', exact: false },
  { path: '/booking-confirmation', exact: false },
  { path: '/my-booking', exact: false },
  { path: '/car-drop/', exact: false },
  { path: '/car-drop/winner/', exact: false },
  { path: '/campaign-cars/', exact: false },
  { path: '/car-drop/terms-and-conditions/', exact: false },
  { path: '/car-drop/winner/terms-and-conditions/', exact: false },
  { path: '/car-drop/winner/declaration/', exact: false },
  { path: '/remind-you-later/', exact: false },
  { path: '/v2/kyc/', exact: false },
  { path: '/v2/car-valuation', exact: false },
  { path: '/acko-app', exact: false },
  { path: '/help-me-buy', exact: false },
  { path: '/callback-confirmation', exact: false },
  { path: '/see-how-ratings-work', exact: false },
] as const;

type WhenCondition = 'app' | 'mobile' | 'appOrMobile';

const CONDITIONAL_HIDE_RULES: readonly {
  when: WhenCondition;
  paths: readonly { path: string; exact?: boolean }[];
}[] = [
  { when: 'appOrMobile', paths: [{ path: '/wishlist', exact: false }] },
  { when: 'appOrMobile', paths: [{ path: '/my-quotes', exact: false }] },
  {
    when: 'app',
    paths: [
      { path: '/compare', exact: false },
      { path: '/terms-and-conditions', exact: false },
    ],
  },
];

export const useHideFooter = (serverIsInApp?: boolean): boolean => {
  const pathname = usePathname();
  const isMobile = useIsMobile();
  const clientIsInApp = inApp();
  const isInApp = serverIsInApp ?? clientIsInApp;

  if (pathMatchesAny(pathname, ALWAYS_HIDE_PATHS)) {
    return true;
  }

  const conditionMatches = (when: WhenCondition): boolean => {
    switch (when) {
      case 'app':
        return isInApp;
      case 'mobile':
        return isMobile;
      case 'appOrMobile':
        return isInApp || isMobile;
      default:
        return false;
    }
  };

  const hidesByCondition = CONDITIONAL_HIDE_RULES.some(
    (rule) =>
      conditionMatches(rule.when) && pathMatchesAny(pathname, rule.paths)
  );

  return hidesByCondition;
};
